import { useEffect, useRef, useState } from 'react';

import useBlackboxVersion from './useBlackboxVersion';
import { useSessionCache } from './useSessionCache';

/**
 * A wrapper around useBlackboxVersion that caches the isOnDevelopment value
 * to prevent repeated network requests when used inside components that re-render frequently.
 *
 * This hook uses the SessionCache context to cache just the isOnDevelopment flag,
 * while still calling the original hook to maintain all other functionality.
 */
export function useCachedBlackboxVersion(): ReturnType<typeof useBlackboxVersion> {
  const { getValue } = useSessionCache();
  const blackboxVersionData = useBlackboxVersion();
  const [cachedIsOnDevelopment, setCachedIsOnDevelopment] = useState<boolean | undefined>(
    undefined,
  );
  const hasCached = useRef(false);

  useEffect(() => {
    async function cacheDevelopmentStatus(): Promise<void> {
      if (!hasCached.current && blackboxVersionData.blackboxVersion) {
        const isOnDev = await getValue('isOnDevelopment', () => {
          return blackboxVersionData.isOnDevelopment;
        });
        setCachedIsOnDevelopment(isOnDev as boolean);
        hasCached.current = true;
      }
    }

    cacheDevelopmentStatus();
  }, [blackboxVersionData.blackboxVersion, blackboxVersionData.isOnDevelopment, getValue]);

  // Return the original data but with the cached isOnDevelopment value
  return {
    ...blackboxVersionData,
    isOnDevelopment:
      cachedIsOnDevelopment !== undefined
        ? cachedIsOnDevelopment
        : blackboxVersionData.isOnDevelopment,
  };
}
